import axios from "axios";

import { useSWRMutate } from "./useSWR";
import setCookie from "./setCookie";
import getSessionData from "./getSessionData";
import t from "./translate";
import routes from "routes";
import { fetchClinicalEligibility, fetchProfile } from "./assessment";
import { getRecaptchaToken } from "./getRecaptchaToken";
import { REGISTER_RECAPTCHA_ACTION_KEY } from "./constants";
import { logger } from "./logger";
import { getSequenceRedirectUrl } from "./sequence";

export const ACTIVE = "ACTIVE";
export const LAPSED = "LAPSED";
export const REGISTERED = "classic-registered";
export const IAP_IOS = "classic-ios";
export const IAP_ANDROID = "classic-android";
export const CLINICAL = "clinical";
export const ACTIVE_SUBSCRIPTION = "subscription";
export const IAP_SUBSCRIPTION = "external_subscription";
export const clinicalBasePath = "/us/personalassessment/clinical";
export const uni3BasePath = "/us/personalassessment/var3-vs";
export const uni4BasePath = "/us/personalassessment/var4-vs";
export const clinical2BasePath = "/us/personalassessment/clinical-v2";
export const foodNoiseBasePath = "/us/personalassessment/food-noise";
export const initialPath = `${clinicalBasePath}/first-name`;
export const initialPathUni3 = `${uni3BasePath}/welcome`;
export const initialPathUni4 = `${uni4BasePath}/welcome`;
export const initialPathClinical2 = `${clinical2BasePath}/welcome`;
export const initialPathFoodNoise = `${foodNoiseBasePath}/welcome`;
/**
 * Creates a new user account with the provided data.
 * @async
 * @param {Object} data - The data for creating the account.
 * @param {string} data.email - The email address of the user.
 * @param {string} data.password - The password for the user account.
 * @param {string} data.firstname - The first name of the user.
 * @param {string} data.lastname - The last name of the user.
 * @returns {Promise<Object>} A promise that resolves with the account creation response data.
 * @throws {Error} Throws an error if any required fields are missing or if there is an error creating the account.
 */
export const createAccount = async (key, { arg: data }) => {
  const { email, password, firstname, lastname, optin } = data;

  if (!email || !password || !firstname || !lastname) {
    throw new Error("Missing required fields");
  }

  try {
    const recaptchaToken = await getRecaptchaToken(
      REGISTER_RECAPTCHA_ACTION_KEY
    );

    logger("Creating account", {
      level: "info"
    });

    const response = await axios({
      timeout: 10000,
      method: "POST",
      url: `${window.ww.wwAPI}/account/v1/profile/register`,
      params: {
        market: "en-US",
        channel: "email"
      },
      data: {
        email,
        password,
        firstname,
        lastname,
        optin: optin ? "email" : "none",
        timezone: "-04:00"
      },
      ...(recaptchaToken
        ? { headers: { "X-Recaptcha-Token": recaptchaToken } }
        : {})
    });
    const { data } = response;

    if (data.id_token) {
      setCookie("wwSession", data.id_token);

      logger("Sucessfully created account", {
        level: "info"
      });
    } else {
      logger("Error creating account", {
        level: "info",
        customAttributes: { error: "No id_token in response" }
      });
      throw new Error("No id_token in response");
    }

    return response;
  } catch (error) {
    // Log users entering an email that is already registered as warning instead of error.
    const level = error?.response?.status === 409 ? "warn" : "error";
    logger("Error creating account", {
      level,
      customAttributes: { error }
    });
    throw error?.response || error?.message || "Error creating account";
  }
};

/**
 * Updates the core profile information.
 * @async
 * @param {Object} data - The data containing profile information to be updated.
 * @param {string} data.phoneNumber - The phone number to update.
 * @param {string} data.addressLine1 - The first line of the address.
 * @param {string} data.city - The city of the address.
 * @param {string} data.state - The state of the address.
 * @param {string} data.zipcode - The zipcode of the address.
 * @returns {Promise<Object>} A promise that resolves with the updated profile data.
 * @throws {Error} Throws an error if UUID or token is missing, or if there are missing required fields.
 */
export const updateCoreProfile = async (key, { arg: data }) => {
  const { uuid, token } = getSessionData();
  if (!uuid) throw new Error("No uuid found in token");
  if (!token) throw new Error("No token found");

  const {
    firstName,
    lastName,
    phone,
    street1,
    street2 = "",
    city,
    region,
    postalCode,
    country = "US"
  } = data;

  if (!phone || !street1 || !city || !region || !postalCode) {
    throw new Error("Missing required fields");
  }

  try {
    logger("Updating core profile", {
      level: "info"
    });

    const response = await axios({
      timeout: 10000,
      method: "POST",
      url: `${window.ww.wwAPI}/sms/v1/accounts/${uuid}/shippingAddresses?locale=en-us`,
      headers: {
        Authorization: `Bearer ${token}`
      },
      data: {
        firstName,
        ...(lastName && { lastName }),
        phone,
        street1,
        ...(street2 && { street2 }),
        city,
        region,
        postalCode,
        country
      }
    });

    logger("Successfully updated core profile", {
      level: "info"
    });

    return response;
  } catch (error) {
    logger("Error updating core profile", {
      level: "info",
      customAttributes: { error }
    });
    throw error?.response || error?.message || "Error updating core profile";
  }
};

// Active users are redirected to the clinic app url.
const getClinicAppRedirectUrl = async () => {
  try {
    const sequenceRedirectUrl = await getSequenceRedirectUrl();
    const { redirect } = sequenceRedirectUrl.data;

    if (redirect) {
      return redirect;
    }

    return t("CLINIC_APP_URL");
  } catch (error) {
    logger("Error getting clinic tab init redirect url", {
      level: "error",
      customAttributes: { error }
    });

    return t("CLINIC_APP_URL");
  }
};

/**
 * Fetches profile data and evaluates the logged-in user's sequence subscription status.
 *
 * If the user has an active sequence subscription, they are redirected to a predefined sequence URL.
 *
 * @async
 * @returns {Promise<Object>} A promise that resolves with the profile data.
 * @throws {Error} Throws an error if UUID or token is missing, or if there is an error fetching email data.
 */
export const getLoggedInAs = async () => {
  const { uuid, token } = getSessionData();

  if (!uuid || !token) return;

  try {
    logger("Getting client user profile", {
      level: "info"
    });

    const clientProfile = await axios({
      timeout: 10000,
      method: "GET",
      url: `${window.ww.wwAPI}/v1/subscription/${uuid}/profile?market=en-us`,
      headers: {
        Authorization: `Bearer ${token}`
      }
    });

    logger("Sucessfully got client user profile", {
      level: "info"
    });

    logger("Getting server profile data", {
      level: "info"
    });

    const memberProfile = await fetchProfile();

    logger("Successfully got server profile data", {
      level: "info"
    });

    const { entitlements, additionalSettings, subscriptions, profileData } =
      memberProfile?.data?.profile || {};
    const { height, gender } = profileData || {};
    const seqAssessmentId = additionalSettings?.seqAssessmentId;
    const isRegistered = entitlements?.includes(REGISTERED);
    const isB2B = !!additionalSettings?.classicSettingsClientName?.trim();
    // If an IAP user has an active core/premium subscription (ACTIVE_SUBSCRIPTION),
    // do NOT treat them like an IAP user - send them to switch.
    const isIAP =
      (entitlements?.includes(IAP_IOS) ||
        entitlements?.includes(IAP_ANDROID)) &&
      !subscriptions.find(sub => sub.object === ACTIVE_SUBSCRIPTION);
    let isLapsed = false;

    const isActiveUser = isIAP === false && isRegistered === false;

    try {
      logger("Getting clinical eligibility", {
        level: "info"
      });

      const memberData = await fetchClinicalEligibility([null, "en-US"]);

      logger("Successfully got clinical eligibility", {
        level: "info"
      });

      // Active sequence users get sent to the clinic app url.
      // This currently only triggers for legacy sequence B2B users.
      // If a user is an active clinic subscriber,
      // the eligibility API instead returns a 400. See catch block below.
      if (memberData?.data?.sequenceSubscriptionStatus === ACTIVE) {
        const redirectUrl = await getClinicAppRedirectUrl();
        window.location.href = redirectUrl;
      }

      // Lapsed users are users who have a seqAssessmentId and have sequenceSubscriptionStatus LAPSED.
      isLapsed =
        memberData?.data?.sequenceSubscriptionStatus === LAPSED &&
        !!seqAssessmentId;

      const basePaths = [
        clinicalBasePath,
        uni3BasePath,
        uni4BasePath,
        clinical2BasePath
      ];
      const onTreatmentOptions = basePaths.some(
        path =>
          window.location.pathname ===
          `${path}${routes.clinicCheckoutTreatmentOptions}`
      );
      const onCheckout = basePaths.some(
        path =>
          window.location.pathname === `${path}${routes.clinicCheckoutPlans}`
      );

      if (isLapsed && !onTreatmentOptions && !onCheckout) {
        // Active lapsed users with a b2b status are redirected to switch with b2b offer.
        if (isB2B && isActiveUser) {
          window.location.href = `${t("SWITCH_OFFER")}${t("DEFAULT_B2B_LAPSED_OFFER_PLAN_ELI_APP")}&fromNewUserAssessment=true`;
        }
        // Redirect lapsed users directly to treatment options page.
        else {
          const basePath = basePaths.find(path =>
            window.location.pathname.includes(`${path}/`)
          );

          // The treatment options page will redirect to plans page if compound ineligible.
          window.location.href = `${basePath}${routes.clinicCheckoutTreatmentOptions}`;
        }
      }
    } catch (error) {
      logger("Error checking clinical eligibility", {
        level: "warn",
        customAttributes: { error }
      });

      // If the user is a clinical member, redirect to the clinic app url.
      // Eligibility API returns a 400 if the user is an active clinic subscriber,
      // so this check occurs in the catch block.
      if (entitlements?.includes(CLINICAL)) {
        const redirectUrl = await getClinicAppRedirectUrl();
        window.location.href = redirectUrl;
      }
    }

    const response = {
      data: {
        profileData: {
          ...clientProfile.data,
          height,
          gender
        },
        entitlements: entitlements || [],
        seqAssessmentId,
        isLapsed,
        isRegistered,
        isB2B: isB2B && isActiveUser,
        isIAP
      }
    };

    return new Promise(resolve => {
      resolve(response);
    });
  } catch (error) {
    logger("Error getting profile data", {
      level: "error",
      customAttributes: { error }
    });
    throw error?.response || error?.message || "Error getting profile data";
  }
};

export const loginUrl = redirect =>
  `https://cmx.${
    window.ww.envSubdomain
  }weightwatchers.com/login?redirect_uri=${encodeURIComponent(
    redirect || window.location.href
  )}`;

/**
 * Logs the user out by redirecting to the logout endpoint, which in turn redirects to the provided URL.
 * Expiring the auth cookies
 */
export const logout = redirect => {
  window.location.href = `${
    window.ww.wwAuthApi
  }/logout?redirect=${encodeURIComponent(redirect || window.location.href)}`;
};

/**
 * Custom hook for creating a user account.
 * @returns {{
 *   trigger: Function,
 *   isMutating: boolean,
 *   data: Object,
 *   error: Object
 * }} Object containing a function to trigger the api call, along with loading state, data, and error.
 */
export const useCreateAccount = () =>
  useSWRMutate("createAccount", createAccount, { throwOnError: false });

/**
 * Custom hook for updating the core profile information.
 * @returns {{
 *   trigger: Function,
 *   isMutating: boolean,
 *   data: Object,
 *   error: Object
 * }} Object containing a function to trigger the api call, along with loading state, data, and error.
 */
export const useUpdateCoreProfile = () =>
  useSWRMutate("updateCoreProfile", updateCoreProfile, { throwOnError: false });

/**
 * Custom hook for getting the logged-in user information.
 * @returns {{
 *   trigger: Function,
 *   isMutating: boolean,
 *   data: Object,
 *   error: Object
 * }} Object containing a function to trigger the api call, along with loading state, data, and error.
 */
export const useGetLoggedInAs = () =>
  useSWRMutate("getLoggedInAs", getLoggedInAs, { throwOnError: false });
